'use client';
import { FC, useCallback } from 'react';

import styled from '@emotion/styled';
import { disconnect } from '@wagmi/core';
import { useTranslations } from 'next-intl';
import { useBoolean } from 'usehooks-ts';
import type { Address } from 'viem';
import { isAddressEqual } from 'viem';
import { useAccount } from 'wagmi';

import { ConnectWallet } from '@web/features/settings/ConnectWallet';
import { CopyWallet } from '@web/features/settings/CopyWallet';
import { ProfileForm } from '@web/features/settings/ProfileForm';
import { useCurrentCompletedUserOrThrow } from '@web/features/user/service';
import { Button, showSnackbar } from '@web/ui/components';
import {
  ConfirmDialog,
  ConfirmDialogContent,
  ConfirmDialogTitle,
} from '@web/ui/components/modal/ConfirmDialog';
import { TwitterIcon } from '@web/ui/icons';
import { wagmiConfig } from '@web/utils/rainbowkit/WagmiProvider';
import { trpcReact } from '@web/utils/trpc/trpcReact';

const SettingsPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-inline: ${({ theme }) => theme.spacing('0500')};
  gap: ${({ theme }) => theme.spacing('0500')};
  background: ${({ theme }) => theme.color.background};
  padding-bottom: ${({ theme }) => theme.spacing('2000')};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding-top: ${({ theme }) => theme.spacing('3000')};
    padding-bottom: 220px;
    gap: ${({ theme }) => theme.spacing('1000')};
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: fit-content;
  background: ${({ theme }) => theme.color.surfaceContainerHigh};
  border-radius: ${({ theme }) => theme.shape.corner['1250']};
  padding: ${({ theme }) => theme.spacing('2000')};
  gap: ${({ theme }) => theme.spacing('2000')};
  box-sizing: border-box;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-inline: auto;
    box-sizing: border-box;
    width: 100%;
    max-width: 800px;
  }
`;

const Title = styled.p`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['3']};
`;

const BlockContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('1500')};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const BlockRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing('1000')};
  align-items: center;
`;

const BlockText = styled.span`
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  color: ${({ theme }) => theme.color.onSurface};
  opacity: ${({ theme }) => theme.opacity.opaque};
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ theme }) => theme.size['2500']};
  height: ${({ theme }) => theme.size['2500']};
  padding: ${({ theme }) => theme.spacing('0500')};
  box-sizing: border-box;
  background: ${({ theme }) => theme.color.inverseSurface};
  color: ${({ theme }) => theme.color.onInverseSurface};
  border-radius: ${({ theme }) => theme.shape.corner['0500']};
`;

const ConfirmDisconnect = styled.span`
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
  color: ${({ theme }) => theme.color.onSurfaceVariant};
`;

const WalletRow: FC<{ wallet: Address; wallets: number }> = ({
  wallet,
  wallets,
}) => {
  const t = useTranslations();
  const web3Account = useAccount();
  const {
    value: showConfirmDisconnect,
    setTrue: openConfirmDisconnect,
    setFalse: closeConfirmDisconnect,
  } = useBoolean(false);
  const disconnectWallet = trpcReact.auth.disconnectWallet.useMutation({
    onSuccess: async () => {
      if (
        web3Account.status === 'connected' &&
        isAddressEqual(web3Account.address, wallet)
      ) {
        disconnect(wagmiConfig);
      }
      await user.invalidate();
      showSnackbar(t('SETTINGS_DISCONNECT_WALLET_SUCCESS'));
    },
  });

  const user = trpcReact.useUtils().user;
  const handleDisconnectWallet = useCallback(
    () => disconnectWallet.mutate(wallet),
    [wallet],
  );

  return (
    <BlockContainer>
      <CopyWallet wallet={wallet} fullWidth />
      <Button
        variant="secondary"
        background="surfaceContainerHigh"
        color="onSurface"
        label={t('SETTINGS_PAGE_ACTION_DISCONNECT')}
        onClick={openConfirmDisconnect}
        disabled={wallets <= 1}
      />
      <ConfirmDialog
        confirm={{
          onClick: handleDisconnectWallet,
          label: t(
            disconnectWallet.isPending
              ? 'SETTINGS_PAGE_ACTION_DISCONNECTING'
              : 'SETTINGS_DISCONNECT_WALLET_CONFIRM',
          ),
          disabled: disconnectWallet.isPending,
        }}
        cancel={{
          label: t('SETTINGS_DISCONNECT_WALLET_CANCEL'),
          disabled: disconnectWallet.isPending,
        }}
        open={showConfirmDisconnect}
        onClose={closeConfirmDisconnect}
      >
        <ConfirmDialogTitle>
          {t('SETTINGS_DISCONNECT_WALLET_CONFIRM_TITLE')}
        </ConfirmDialogTitle>
        <ConfirmDialogContent>
          <ConfirmDisconnect>
            {t('SETTINGS_DISCONNECT_WALLET_CONFIRM_CONTENT')}
          </ConfirmDisconnect>
          <CopyWallet wallet={wallet} />
        </ConfirmDialogContent>
      </ConfirmDialog>
    </BlockContainer>
  );
};

export default function SettingsPage() {
  const t = useTranslations();
  const currentUser = useCurrentCompletedUserOrThrow();
  const userContext = trpcReact.useUtils().user;

  const disconnectTwitter =
    trpcReact.user.twitter.disconnectTwitter.useMutation({
      onError: () => {
        showSnackbar(t('SETTINGS_DISCONNECT_TWITTER_ERROR'));
      },
      onSuccess: () => {
        showSnackbar(t('SETTINGS_DISCONNECT_TWITTER_SUCCESS'));
        userContext.invalidate();
      },
    });

  const handleDisconnectTwitter = useCallback(async () => {
    disconnectTwitter.mutate(undefined as never);
  }, []);

  return (
    <SettingsPageContainer>
      <ContentContainer>
        <Title>{t('SETTINGS_PAGE_PROFILE')}</Title>
        <ProfileForm />
      </ContentContainer>
      <ContentContainer>
        <Title>{t('SETTINGS_PAGE_CONNECTIONS')}</Title>
        <BlockContainer>
          <BlockRow>
            <IconContainer>
              <TwitterIcon />
            </IconContainer>
            <BlockText>@{currentUser.user.twitter.handle}</BlockText>
          </BlockRow>
          <Button
            label={t('SETTINGS_PAGE_ACTION_DISCONNECT')}
            onClick={handleDisconnectTwitter}
          />
        </BlockContainer>
      </ContentContainer>
      <ContentContainer>
        <Title>{t('SETTINGS_PAGE_WALLET')}</Title>
        {currentUser.user.wallets.map((wallet) => (
          <WalletRow
            key={wallet}
            wallet={wallet}
            wallets={currentUser.user.wallets.length}
          />
        ))}
        <BlockContainer>
          <ConnectWallet />
        </BlockContainer>
      </ContentContainer>
    </SettingsPageContainer>
  );
}
